import { FormEvent, useMemo, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { FunnelSimple, MagnifyingGlass, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import useCaseTickets, { type CaseTicketStatus } from "../hooks/useCaseTickets";
import useCaseTicket from "../hooks/useCaseTicket";
import {
  useCreateCaseTicket,
  useAddCaseTicketMessage,
} from "../hooks/useCaseManagementActions";

const Support = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState<CaseTicketStatus | "">("");
  const [showFilters, setShowFilters] = useState(false);
  const [message, setMessage] = useState("");

  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newCategory, setNewCategory] = useState("PAYMENT");
  const [newSubject, setNewSubject] = useState("");
  const [newDescription, setNewDescription] = useState("");

  const { items, isLoading, isError, error, refetch } = useCaseTickets({
    q: searchTerm || undefined,
    status: status || undefined,
    page: 1,
    limit: 100,
  });

  const { ticket, isLoading: isTicketLoading, refetch: refetchTicket } = useCaseTicket(id, {
    refetchInterval: 10000,
  });

  const filteredTickets = useMemo(() => items, [items]);

  const { addCaseTicketMessage, isSendingMessage } = useAddCaseTicketMessage();
  const { createCaseTicket, isCreatingTicket } = useCreateCaseTicket();

  const onSendMessage = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || !id) return;
    addCaseTicketMessage({ ticketId: id, message: trimmed }, {
      onSuccess() {
        setMessage("");
        toast.success("Message sent");
        refetchTicket();
        refetch();
      },
      onError(err: any) {
        toast.error(err?.message || "Failed to send message");
      },
    });
  };

  const onCreateTicket = (e: FormEvent) => {
    e.preventDefault();
    if (!newSubject.trim()) {
      toast.error("Subject is required");
      return;
    }
    createCaseTicket({
      category: newCategory,
      subject: newSubject.trim(),
      description: newDescription.trim() || undefined,
      channel: "WEB",
      priority: "MEDIUM",
    }, {
      onSuccess(res) {
        const newId = res?.data?.id;
        toast.success("Ticket created");
        setShowNewTicket(false);
        setNewSubject("");
        setNewDescription("");
        refetch();
        if (newId) navigate(`/support/${newId}`);
      },
      onError(err: any) {
        toast.error(err?.message || "Failed to create ticket");
      },
    });
  };

  return (
    <div className="flex flex-col md:grid md:grid-cols-6 md:grid-rows-1 relative items-start h-full flex-1 min-h-[70vh]">
      <div className="flex flex-col gap-3 px-2 md:overflow-y-auto md:col-span-2 relative pt-4 md:h-full custom-scroll-bar bg-gray-50 border-b md:border-b-0 md:border-r">
        <div className="p-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center border border-gray-300 rounded-lg px-3 flex-1 bg-white">
            <MagnifyingGlass size={18} className="text-gray-400" />
            <input
              className="flex-1 p-2 focus:outline-none text-sm"
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by ticket number, subject, category"
            />
          </div>

          <button
            onClick={() => setShowFilters((prev) => !prev)}
            className="p-3 px-4 text-sm border-gray-300 border rounded-lg font-medium flex items-center gap-2 hover:bg-gray-100 transition"
          >
            <FunnelSimple size={18} /> Filters
          </button>

          <button
            onClick={() => setShowNewTicket((prev) => !prev)}
            className="p-3 px-4 text-sm rounded-lg font-medium flex items-center gap-2 bg-deepBlue text-white"
          >
            <Plus size={18} /> New
          </button>
        </div>

        {showFilters && (
          <div className="border-t p-4 bg-gray-100 flex flex-col gap-4">
            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-1">Status</label>
              <select
                className="border rounded-md p-2 text-sm bg-white"
                value={status}
                onChange={(e) => setStatus(e.target.value as CaseTicketStatus | "")}
              >
                <option value="">All</option>
                <option value="OPEN">OPEN</option>
                <option value="IN_REVIEW">IN_REVIEW</option>
                <option value="PENDING_USER">PENDING_USER</option>
                <option value="PENDING_PARTNER">PENDING_PARTNER</option>
                <option value="ESCALATED">ESCALATED</option>
                <option value="RESOLVED">RESOLVED</option>
                <option value="REJECTED">REJECTED</option>
                <option value="CLOSED">CLOSED</option>
              </select>
            </div>

            <button
              onClick={() => {
                setStatus("");
                setShowFilters(false);
                refetch();
              }}
              className="border border-gray-400 hover:bg-gray-200 rounded-md px-4 py-2 text-sm flex items-center justify-center gap-1"
            >
              <X size={14} /> Reset
            </button>
          </div>
        )}

        {showNewTicket && (
          <form onSubmit={onCreateTicket} className="mx-3 mb-2 p-3 bg-white border rounded-lg flex flex-col gap-2">
            <div className="text-sm font-semibold">Create Ticket</div>
            <select
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              className="border rounded-md p-2 text-sm"
            >
              <option value="PAYMENT">PAYMENT</option>
              <option value="WALLET">WALLET</option>
              <option value="TRANSFER">TRANSFER</option>
              <option value="LOAN">LOAN</option>
              <option value="BNPL">BNPL</option>
              <option value="GENERAL">GENERAL</option>
            </select>
            <input
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              placeholder="Subject"
              className="border rounded-md p-2 text-sm"
            />
            <textarea
              rows={3}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Describe your issue"
              className="border rounded-md p-2 text-sm"
            />
            <button
              type="submit"
              disabled={isCreatingTicket}
              className="bg-visaro-orange text-white rounded-md px-3 py-2 text-sm disabled:opacity-50"
            >
              {isCreatingTicket ? "Creating..." : "Create ticket"}
            </button>
          </form>
        )}

        <div className="flex flex-col gap-0">
          {isLoading ? (
            <div className="p-10 text-center text-gray-500 text-sm">Loading tickets...</div>
          ) : isError ? (
            <div className="p-6 text-sm text-red-600">
              <div>Failed to load tickets: {error?.message || "Unknown error"}</div>
              <button
                onClick={() => refetch()}
                className="mt-3 px-3 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-xs"
              >
                Retry
              </button>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="p-10 text-center text-gray-500 text-sm">No tickets found.</div>
          ) : (
            filteredTickets.map((entry) => (
              <NavLink
                to={`/support/${entry.id}`}
                key={entry.id}
                className={({ isActive }) =>
                  `p-3 bg-white hover:bg-gray-50 cursor-pointer border-b flex justify-between items-center transition-all ${
                    isActive ? "ring-1 ring-visaro-orange" : ""
                  }`
                }
              >
                <div>
                  <div className="font-medium flex items-center gap-2">{entry.subject}</div>
                  <div className="text-sm text-gray-500">
                    {entry.ticketNumber} • {entry.category}
                  </div>
                </div>

                <span
                  className={`font-medium text-xs px-2 py-1 rounded-lg ${
                    entry.status === "RESOLVED" || entry.status === "CLOSED"
                      ? "text-gray-600 bg-gray-100"
                      : entry.status === "ESCALATED"
                      ? "text-orange-700 bg-orange-100"
                      : "text-green-700 bg-green-100"
                  }`}
                >
                  {entry.status}
                </span>
              </NavLink>
            ))
          )}
        </div>
      </div>

      <div className="md:border-l border-gray-200 md:overflow-y-auto top-0 flex-1 md:col-span-4 md:sticky md:h-full custom-scroll-bar bg-white">
        {!id ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 py-20 w-full flex-1">
            <MagnifyingGlass size={48} className="mb-4 text-gray-300" />
            <div className="text-lg font-semibold mb-2">No ticket selected</div>
            <div className="text-sm">Select a ticket from the list to view its details.</div>
          </div>
        ) : isTicketLoading ? (
          <div className="h-full flex items-center justify-center text-gray-500">Loading ticket details...</div>
        ) : !ticket ? (
          <div className="h-full flex items-center justify-center text-gray-500">Ticket not found</div>
        ) : (
          <div className="bg-gray-100 h-full flex-1 flex flex-col">
            <div className="flex items-center gap-2 border-b p-3 bg-white">
              <div className="flex flex-col">
                <div className="font-semibold">{ticket.subject}</div>
                <div className="text-xs text-gray-500">
                  {ticket.ticketNumber} • {ticket.status} • {ticket.priority}
                </div>
              </div>
            </div>

            <div className="p-3 border-b bg-gray-50 text-xs text-gray-600 grid grid-cols-2 gap-2">
              <div>
                <span className="font-medium">Category:</span> {ticket.category}
              </div>
              <div>
                <span className="font-medium">Customer:</span> {user?.firstname || ""} {user?.lastname || ""}
              </div>
              <div className="col-span-2">
                <span className="font-medium">Description:</span> {ticket.description || "No description"}
              </div>
            </div>

            <div className="p-4 flex flex-col gap-3 overflow-y-auto flex-1">
              {ticket.messages?.length ? (
                ticket.messages.map((msg) => {
                  const isSystem = msg.authorType === "SYSTEM";
                  const isUserMessage =
                    msg.authorType === "USER" ||
                    (!!user?.id && !!msg.authorId && msg.authorId === user.id);
                  const bubbleStyles = isSystem
                    ? "bg-gray-200 text-gray-800 mx-auto"
                    : isUserMessage
                    ? "bg-[#EA5C29] text-white ml-auto"
                    : "bg-blue-50 text-blue-900 border border-blue-100 mr-auto";

                  return (
                    <div key={msg.id} className={`max-w-xl px-4 py-2 rounded-lg shadow ${bubbleStyles}`}>
                      <div className="text-sm whitespace-pre-line">{msg.message}</div>
                      <div className="text-[11px] mt-1 opacity-80">
                        {isSystem ? "SYSTEM" : isUserMessage ? "YOU" : "SUPPORT"} • {msg.visibility}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center text-sm text-gray-500 py-10">No messages yet for this ticket.</div>
              )}
            </div>

            <form onSubmit={onSendMessage} className="flex items-center gap-2 p-4 border-t bg-white">
              <textarea
                rows={1}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message..."
                className="py-2 px-3 border rounded-lg bg-transparent flex-1 focus:outline-none resize-none"
              />

              <button
                type="submit"
                disabled={isSendingMessage || !message.trim()}
                className="px-4 py-2 bg-visaro-orange text-white rounded-lg text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isSendingMessage ? "Sending..." : "Send"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Support;
